<template>
  <div>
    <!-- search -->
    <search-nav @handleSearch="onClickSearch">
      <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
        <component
          v-for="(component, index) in stdSearchComponents"
          v-if="showSearchNavItem(component['options'])"
          :is="component['component']"
          :label="component['label']"
          :query="component['query']"
          :options="component['options']"
          :onClickSearch="onClickSearch"
          v-model="nav.value[component['value']]"
        />
      </div>
    </search-nav>

    <vs-card class="w-full">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-baseline flex-wrap gap-x-3">
          <span style="font-size: 18px; font-weight: 500">{{ `입문: ${입문수}명 출문: ${출문수}명 미출문: ${미출문수}명 ` }}</span>
          <!-- 실제로 조회된 기간. 한쪽 날짜만 비어 있으면 하루만 조회된다는 안내를 덧붙인다 -->
          <span class="range-info">
            조회기간 {{ loadedRangeLabel }}
            <template v-if="dateRangeHint">({{ dateRangeHint }})</template>
          </span>
        </div>
        <div class="flex gap-2">
          <vs-button color="secondary" type="border" :disabled="isLoading || sortedList.length === 0" @click="onClickExcel">
            <div class="flex gap-2"><feather-icon icon="DownloadIcon" svgClasses="h-5 w-5" /> Excel 다운로드</div>
          </vs-button>
          <vs-button color="secondary" type="border" @click="onClickRefresh">
            <div class="flex gap-2"><feather-icon icon="RotateCwIcon" svgClasses="h-5 w-5" /> 갱신</div>
          </vs-button>
        </div>
      </div>

      <!-- 로딩 화면 -->
      <div v-if="isLoading" class="loading-container">
        <vs-progress indeterminate color="primary"></vs-progress>
        <p class="loading-text">데이터를 불러오는 중입니다...</p>
      </div>

      <!-- 데이터 테이블 -->
      <div v-else>
        <table>
          <colgroup>
            <col width="80px" />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col width="100px" />
            <col v-if="canViewPhone" />
            <col />
            <col />
            <col width="80px" />
          </colgroup>
          <thead>
            <tr>
              <th>No.</th>
              <th class="sortable" @click="onClickSort('regionName')">
                사업장명
                <span class="sort-icon" v-if="sortKey === 'regionName'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('companyName')">
                업체명
                <span class="sort-icon" v-if="sortKey === 'companyName'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('permitName')">
                공사/작업명
                <span class="sort-icon" v-if="sortKey === 'permitName'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('workPeriod')">
                작업기간
                <span class="sort-icon" v-if="sortKey === 'workPeriod'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('userName')">
                이름
                <span class="sort-icon" v-if="sortKey === 'userName'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('birth')">
                생년월일
                <span class="sort-icon" v-if="sortKey === 'birth'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th v-if="canViewPhone" class="sortable" @click="onClickSort('phone')">
                휴대폰번호
                <span class="sort-icon" v-if="sortKey === 'phone'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('enterTime')">
                입문시간
                <span class="sort-icon" v-if="sortKey === 'enterTime'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('exitTime')">
                출문시간
                <span class="sort-icon" v-if="sortKey === 'exitTime'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
              <th class="sortable" @click="onClickSort('status')">
                구분
                <span class="sort-icon" v-if="sortKey === 'status'">
                  {{ sortOrder === "asc" ? "▲" : "▼" }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedList.length === 0">
              <td :colspan="canViewPhone ? 11 : 10" class="empty-message">조회된 데이터가 없습니다.</td>
            </tr>
            <tr v-for="(item, index) in paginatedList" :key="index">
              <td>{{ (page - 1) * perPage + index + 1 }}</td>
              <td>{{ item.regionName }}</td>
              <td>{{ item.companyName }}</td>
              <td>{{ item.permitName }}</td>
              <td>{{ item.workPeriod }}</td>
              <td>{{ item.userName }}</td>
              <td>{{ item.birth }}</td>
              <td v-if="canViewPhone">{{ item.phone }}</td>
              <td>{{ item.enterTime }}</td>
              <td>{{ item.exitTime }}</td>
              <td>{{ getStatus(item) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="vs-con-table" v-if="paginatedList.length > 0">
          <div class="con-pagination-table vs-table--pagination">
            <k-pagination :total="lastPage" v-model="page" @change="onChangePage"></k-pagination>
          </div>
        </div>
      </div>
    </vs-card>

    <!-- Excel 다운로드 사유 입력 (개인정보 접속기록) -->
    <vs-popup title="Excel 다운로드 사유" :active.sync="excelReasonPopup">
      <p class="mb-2">이름·생년월일 등 개인정보가 포함된 파일입니다. 다운로드 사유를 입력해 주세요.</p>
      <vs-textarea v-model="excelReason" :counter="500" rows="4" placeholder="예) 10월 출입 인원 현황 보고 (5자 이상)" />
      <div class="flex justify-end gap-2">
        <vs-button color="secondary" type="border" @click="excelReasonPopup = false">취소</vs-button>
        <vs-button color="primary" :disabled="isExcelDownloading || excelReason.trim().length < 5" @click="onConfirmExcel">다운로드</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import SearchColumnKeyword from "@/views/list/components/SearchColumnKeyword.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import searchComponent from "@/views/list/components/include";
import KPagination from "@/components/kPagination/kPagination.vue";
import HttpRequest from "@/util/HttpRequest";
import ExcelJS from "exceljs";
import excelSupporter from "@/excelSupporter";

export default {
  components: {
    KPagination,
    FieldSelector,
    SearchColumnKeyword,
    SearchNav,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    header: Object,
    value: Object,
    mode: {
      type: String,
      default: "list",
    },
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return 470;
    },
    filteredList() {
      const search = this.nav.value;
      let filtered = [...this.allData];

      // 사업장명 필터 (region_name으로 필터링)
      if (search.field && search.field.fieldName) {
        filtered = filtered.filter((item) => item.regionName && item.regionName === search.field.fieldName);
      }

      // 업체명 필터
      if (search.업체명) {
        filtered = filtered.filter((item) => item.companyName && item.companyName.includes(search.업체명));
      }

      // 이름 필터
      if (search.이름) {
        filtered = filtered.filter((item) => item.userName && item.userName.includes(search.이름));
      }

      // 공사/작업명 필터
      if (search.공사작업명) {
        filtered = filtered.filter((item) => item.permitName && item.permitName.includes(search.공사작업명));
      }

      // 구분 필터
      if (search.구분) {
        filtered = filtered.filter((item) => {
          if (search.구분 === "입문") {
            // 입문 시간이 존재하는 데이터
            return item.enterTime !== "";
          } else if (search.구분 === "출문") {
            // 출문 시간이 존재하는 데이터
            return item.exitTime !== "";
          } else if (search.구분 === "미출문") {
            // 입문했지만 출문하지 않은 데이터
            return item.enterTime && !item.exitTime;
          }
          return true;
        });
      }

      return filtered;
    },
    sortedList() {
      const sorted = [...this.filteredList];

      if (this.sortKey) {
        sorted.sort((a, b) => {
          let aVal, bVal;

          if (this.sortKey === "status") {
            // 구분 정렬
            aVal = this.getStatus(a);
            bVal = this.getStatus(b);
          } else {
            aVal = a[this.sortKey] || "";
            bVal = b[this.sortKey] || "";
          }

          // 문자열 비교
          if (typeof aVal === "string" && typeof bVal === "string") {
            const comparison = aVal.localeCompare(bVal, "ko");
            return this.sortOrder === "asc" ? comparison : -comparison;
          }

          // 일반 비교
          if (aVal < bVal) return this.sortOrder === "asc" ? -1 : 1;
          if (aVal > bVal) return this.sortOrder === "asc" ? 1 : -1;
          return 0;
        });
      }

      return sorted;
    },
    paginatedList() {
      const start = (this.page - 1) * this.perPage;
      const end = start + this.perPage;
      return this.sortedList.slice(start, end);
    },
    // 검색 기간을 항상 "시작일 <= 종료일" 인 yyyy-MM-dd 쌍으로 정규화한다 (한쪽만 비어 있으면 다른 쪽으로 채움)
    dateRange() {
      const today = new Date().format("yyyy-MM-dd");
      const date = this.nav.value.date || {};
      const sd = `${date.startDate || ""}`.trim();
      const ed = `${date.endDate || ""}`.trim();
      let startDate = sd || ed || today;
      let endDate = ed || sd || today;
      if (startDate > endDate) {
        [startDate, endDate] = [endDate, startDate];
      }
      return { startDate, endDate };
    },
    // 제목·파일명에 쓰는 기간 표시 (하루면 날짜 하나만)
    dateRangeLabel() {
      const { startDate, endDate } = this.dateRange;
      return startDate === endDate ? startDate : `${startDate}~${endDate}`;
    },
    // 집계 옆에 보여주는 기간: 표에 실제로 실린 기간(loadedRange)을 우선하고, 아직 없으면 입력 기준
    loadedRangeLabel() {
      const { startDate, endDate } = this.dateRange;
      const [sd, ed] = (this.loadedRange || `${startDate}~${endDate}`).split("~");
      return sd === ed ? sd : `${sd} ~ ${ed}`;
    },
    // 시작일·종료일 중 한쪽만 입력됐을 때 하루만 조회된다는 안내 문구
    dateRangeHint() {
      const date = this.nav.value.date || {};
      const sd = `${date.startDate || ""}`.trim();
      const ed = `${date.endDate || ""}`.trim();
      if (sd && ed) return "";
      if (sd) return "종료일 미입력 시 시작일 하루만 조회";
      if (ed) return "시작일 미입력 시 종료일 하루만 조회";
      return "날짜 미입력 시 오늘 하루만 조회";
    },
    stdSearchComponents() {
      const today = new Date().format("yyyy-MM-dd");
      const stdSearchComponents = [
        {
          component: "search-select-field",
          label: "사업장명",
          value: "field",
          query: { f_id: "fieldId", f_name: "fieldName" },
          options: {
            default: { fieldId: "", fieldName: "" },
            gridTemplateColumns: "60px 1fr",
          },
        },
        {
          // 기간 검색 (시작일 ~ 종료일). 기본값은 오늘 하루
          component: "search-start-end",
          label: "날짜",
          value: "date",
          query: { sd: "startDate", ed: "endDate" },
          options: {
            default: { sd: today, ed: today },
            maxDate: today, // 오늘 날짜까지만 선택 가능
          },
        },
        { component: "empty-space" },
        {
          component: "search-keyword",
          label: "업체명",
          value: "업체명",
          query: { k: "업체명" },
          options: {
            default: "",
            gridTemplateColumns: "60px 1fr",
          },
        },
        {
          component: "search-keyword",
          label: "이름",
          value: "이름",
          query: { k: "이름" },
          options: {
            default: "",
          },
        },
        {
          component: "search-keyword",
          label: "공사/작업명",
          value: "공사작업명",
          query: { k: "공사작업명" },
          options: {
            default: "",
          },
        },
        {
          component: "search-select",
          label: "구분",
          value: "구분",
          query: { k: "select" },
          options: {
            default: "",
            items: [
              { text: "전체", value: "" },
              { text: "입문", value: "입문" },
              { text: "출문", value: "출문" },
              { text: "미출문", value: "미출문" },
            ],
          },
        },
      ];

      for (const component of stdSearchComponents) {
        component.component = searchComponent[component["component"]];
      }

      return stdSearchComponents;
    },
  },
  data() {
    return {
      isPrintMode: false,
      chartData: {},
      nav: {
        value: {
          date: { startDate: "", endDate: "" },
          field: {},
          업체명: "",
          이름: "",
          공사작업명: "",
          구분: "",
        },
      },
      loadedRange: "", // 마지막으로 API 를 호출한 기간 (startDate~endDate). 같은 기간이면 재호출하지 않는다

      page: 1,
      lastPage: 1,
      perPage: 20,
      total: 0,
      allData: [], // API에서 가져온 전체 데이터
      loadingPromise: null, // 진행 중인 API 요청 Promise (중복 호출 방지)
      입문수: 0,
      출문수: 0,
      미출문수: 0,
      isLoading: false,

      // 휴대폰번호 열 표시·Excel 휴대폰번호 포함 권한 (서버 응답의 canViewUnmasked)
      // 관리자, MaotConfig 출입기록조회(username)·출입기록조회_업체(company_id)에 등록된 사용자만 true
      canViewPhone: false,

      // Excel 다운로드 사유 입력
      excelReasonPopup: false,
      excelReason: "",
      isExcelDownloading: false,

      // 정렬 관련
      sortKey: "enterTime", // 기본 정렬: 입문시간
      sortOrder: "asc", // 기본 정렬 순서: 오름차순
    };
  },
  watch: {
    page: {
      immediate: true,
      deep: true,
      handler(value) {
        // this.loadHistory();
      },
    },
    dateRange: {
      handler(newVal) {
        // 기간이 바뀌면 API 재호출 (갱신 버튼과 동일한 동작). mounted 의 첫 호출과 겹치지 않게 같은 기간은 건너뛴다
        // 요청 중이면 loadHistory 의 finally 에서 바뀐 기간을 다시 불러온다
        const key = `${newVal.startDate}~${newVal.endDate}`;
        if (this.loadedRange && key !== this.loadedRange && !this.loadingPromise) {
          this.loadHistory();
        }
      },
    },
    "nav.value": {
      deep: true,
      handler() {
        // 검색 조건이 변경되면 페이지네이션 업데이트
        this.$nextTick(() => {
          this.updatePagination();
        });
      },
    },
    filteredList: {
      handler() {
        // 필터링된 결과가 변경되면 통계 업데이트
        this.updateStatistics();
      },
    },
  },
  async mounted() {
    // 메뉴 권한(v)이 없으면 주소를 직접 입력해 들어와도 데이터를 부르지 않고 메인으로 보낸다
    if (!(await this.hasMenuPermission())) {
      this.$vs.notify({
        title: "알림",
        text: "메뉴 접근 권한이 없습니다.",
        color: "warning",
        position: "top-right",
      });
      this.$router.replace({ name: "project-home", params: { proj_id: `${this.projectId}` } });
      return;
    }
    // this.nav.value.date = new Date().format("yyyy-MM-dd")
    this.loadHistory();
  },
  methods: {
    // 사이드바 메뉴와 같은 기준: 이 프로젝트에서 1059 게시판 권한의 v 가 1 이어야 한다 (관리자는 통과)
    async hasMenuPermission() {
      try {
        let memberInfo = this.$store.state.member.memberInfo || {};
        if (!memberInfo.boards) {
          await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
          memberInfo = this.$store.state.member.memberInfo || {};
        }
        const userInfo = this.$store.state.auth.userInfo || {};
        if (parseInt(userInfo.is_superuser) > 0) return true;

        return (memberInfo.boards || []).some(
          (b) => b.pivot && `${b.pivot.board_id || b.id}` === "1059" && `${b.pivot.project_id}` === `${this.projectId}` && parseInt(b.pivot.v) === 1
        );
      } catch (err) {
        console.error("메뉴 권한 확인 오류:", err);
        return false;
      }
    },
    showSearchNavItem(options) {
      if (options && options.visibleKeyValue) {
        for (const keyValue of options.visibleKeyValue) {
          const key = Object.keys(keyValue)[0];
          const value = keyValue[key];
          const navValue = this.findJsonDataWithDottedPath(this.nav.value, key);
          if (navValue !== value) {
            if (Array.isArray(navValue) && navValue.toString() === value) {
              return true;
            }
          } else {
            return true;
          }
        }
        return false;
      }
      return true;
    },
    onClickSearch() {
      // 검색 버튼 클릭 시 페이지만 1로 초기화하고 필터링은 computed에서 처리
      this.page = 1;
      this.updatePagination();
    },
    onClickRefresh() {
      // 갱신 버튼만 API 재호출
      this.loadHistory();
    },

    onClickSort(key) {
      // 같은 컬럼을 클릭하면 정렬 순서 토글
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        // 다른 컬럼을 클릭하면 해당 컬럼으로 정렬하고 오름차순으로 초기화
        this.sortKey = key;
        this.sortOrder = "asc";
      }
      // 정렬 후 첫 페이지로 이동
      this.page = 1;
    },

    updatePagination() {
      // 정렬된 결과에 따라 페이지네이션 업데이트
      this.total = this.sortedList.length;
      this.lastPage = Math.ceil(this.sortedList.length / this.perPage);

      // 현재 페이지가 마지막 페이지를 넘어가면 조정
      if (this.page > this.lastPage && this.lastPage > 0) {
        this.page = this.lastPage;
      }
    },

    /**
     * 시간순 QR 기록을 입문-출문 쌍으로 묶는다.
     * 경비실에서 같은 버튼을 반복해서 누르는 경우가 있어
     * 연속된 입문은 최초 입문, 연속된 출문은 최종 출문을 기준으로 삼는다.
     * 입문 기록 없이 출문만 있는 경우(전날 입문 등)는 입문시간을 비워 둔다.
     */
    buildAccessSessions(qrs) {
      const sessions = [];
      let current = null;

      for (const qr of qrs) {
        if (qr.data === "입문") {
          // 아직 출문하지 않은 입문이 있으면 최초 입문을 유지한다
          if (current && !current.exitTime) {
            continue;
          }
          current = { enterTime: qr.created_at, exitTime: "", postId: qr.post_id };
          sessions.push(current);
        } else if (qr.data === "출문") {
          if (current) {
            // 연속된 출문은 마지막 시간으로 덮어써 최종 출문을 남긴다
            current.exitTime = qr.created_at;
          } else {
            current = { enterTime: "", exitTime: qr.created_at, postId: qr.post_id };
            sessions.push(current);
          }
        }
      }

      return sessions;
    },

    /**
     * QR 출문 기록이 없는(미출문) 행에 허가서(976)에 수기로 저장된 출문시간을 채운다.
     * QR 미스캔 시 안환팀이 허가서 작업자 내역에 출문시간을 직접 입력하는데,
     * 이 값은 QR 이력 API에 없어 허가서 목록(출문 인원)과 이 화면의 출문 여부가 어긋났다.
     * 수기 값은 exitManual 로 구분만 해 두고 화면에는 QR 스캔과 같은 형식으로 표시한다.
     */
    async applyManualExitTimes(rows) {
      const targets = rows.filter((row) => row.enterTime && !row.exitTime && row.postId);
      const postIds = [...new Set(targets.map((row) => row.postId))];
      if (postIds.length === 0) return;

      // 허가서를 한 건씩 조회하지 않고 postIds 로 묶어서 목록 API 한 번에 가져온다
      const CHUNK_SIZE = 100;
      const workersByPost = {};
      for (let i = 0; i < postIds.length; i += CHUNK_SIZE) {
        const chunk = postIds.slice(i, i + CHUNK_SIZE);
        try {
          const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
            boardId: 976,
            projectId: this.projectId,
            page: 1,
            perPage: chunk.length,
            postIds: chunk,
          });
          for (const post of (res && res.data) || []) {
            const content = post && post.post_content ? JSON.parse(post.post_content) : null;
            workersByPost[post.id] = content && content.body && Array.isArray(content.body.작업자내역) ? content.body.작업자내역 : [];
          }
        } catch (e) {
          // 허가서 조회 권한이 없거나 실패하면 QR 기록만으로 표시한다
        }
      }

      for (const row of targets) {
        const worker = (workersByPost[row.postId] || []).find((w) => `${w.userId}` === `${row.userId}`);
        if (!worker || !worker.출문시간) continue;
        // 수기 입력은 시각(HH:mm)만 있고 날짜가 비어 있으므로 입문 날짜를 붙인다
        const date = worker.출문날짜 || `${row.enterTime}`.slice(0, 10);
        row.exitTime = `${date} ${worker.출문시간}`;
        row.exitManual = true;
      }
    },

    updateStatistics() {
      // 한 사람이 하루에 여러 행으로 나올 수 있으므로 인원은 사람(사람·사업장·날짜) 기준으로 센다
      const entered = new Set();
      const exited = new Set();
      const notExited = new Set();

      for (const item of this.filteredList) {
        if (item.enterTime) {
          entered.add(item.personKey);
        }
        if (item.exitTime) {
          exited.add(item.personKey);
        }
        // 미출문: 입문은 했지만 출문하지 않은 경우 (열린 입문은 그날 그 사람의 마지막 행에만 생긴다)
        if (item.enterTime && !item.exitTime) {
          notExited.add(item.personKey);
        }
      }

      // 나갔다가 다시 들어와 아직 안에 있는 사람은 출문이 아닌 미출문으로 센다
      for (const personKey of notExited) {
        exited.delete(personKey);
      }

      this.입문수 = entered.size;
      this.출문수 = exited.size;
      this.미출문수 = notExited.size;
    },

    getStatus(item) {
      return item.enterTime && !item.exitTime ? "미출문" : item.exitTime ? "출문" : "";
    },

    // ─── Excel 다운로드 (사유 입력 → 개인정보 접속기록 → 파일 생성) ───
    // 권한이 없는 사용자도 받을 수 있다. 다만 서버가 휴대폰번호를 빼고 생년월일을 연도만 내려주므로 파일에도 그대로 반영된다
    onClickExcel() {
      this.excelReason = "";
      this.excelReasonPopup = true;
    },

    async onConfirmExcel() {
      const reason = this.excelReason.trim();
      if (reason.length < 5 || this.isExcelDownloading) return;

      const date = this.dateRangeLabel;
      const filename = `출입현황_${date}.xlsx`;
      this.isExcelDownloading = true;
      try {
        // 기록이 남지 않으면 다운로드하지 않는다
        const userIds = [...new Set(this.sortedList.map((row) => row.userId).filter((id) => id))];
        await HttpRequest.post("/api/v1/privacy/access-log", {
          projectId: this.projectId,
          resourceType: "user",
          action: "download",
          reason,
          filename,
          recordCount: this.sortedList.length,
          subjects: userIds.slice(0, 1000).map((id) => ({ type: "user", id: String(id) })),
        });
        await this.downloadExcel(date, filename);
        this.excelReasonPopup = false;
      } catch (err) {
        console.error("Excel 다운로드 오류:", err);
        const message = err && err.response && err.response.data && err.response.data.message;
        this.$vs.notify({
          title: "오류",
          text: message || "Excel 다운로드 중 오류가 발생했습니다.",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.isExcelDownloading = false;
      }
    },

    async downloadExcel(date, filename) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("출입현황");

      const columns = [
        { header: "No.", width: 6, value: (item, idx) => String(idx + 1) },
        { header: "사업장명", width: 14, value: (item) => item.regionName || "" },
        { header: "업체명", width: 20, value: (item) => item.companyName || "" },
        { header: "공사/작업명", width: 30, value: (item) => item.permitName || "" },
        { header: "작업기간", width: 24, value: (item) => item.workPeriod || "" },
        { header: "이름", width: 12, value: (item) => item.userName || "" },
        { header: "생년월일", width: 12, value: (item) => item.birth || "" },
        // 권한자에게만 휴대폰번호 열을 넣는다 (권한이 없으면 서버 응답에 휴대폰번호가 없다)
        ...(this.canViewPhone ? [{ header: "휴대폰번호", width: 16, value: (item) => item.phone || "" }] : []),
        { header: "입문시간", width: 20, value: (item) => item.enterTime || "" },
        { header: "출문시간", width: 20, value: (item) => item.exitTime || "" },
        { header: "구분", width: 10, value: (item) => this.getStatus(item) },
      ];

      const colLetter = (n) => String.fromCharCode(64 + n);
      const colgroup = columns.map((c, i) => `${colLetter(i + 1)}:${colLetter(i + 1)}`);
      columns.forEach((c, i) => {
        worksheet.getColumn(colLetter(i + 1)).width = c.width;
      });

      // 제목 + 집계
      excelSupporter.addHeader(worksheet, [{ value: `출입현황 (${date})`, style: "borderHead", merge: `A:${colLetter(columns.length)}` }]);
      const summary = `입문: ${this.입문수}명 / 출문: ${this.출문수}명 / 미출문: ${this.미출문수}명`;
      excelSupporter.addHeader(worksheet, [{ value: summary, style: "borderHead", merge: `A:${colLetter(columns.length)}` }]);
      excelSupporter.addHeader(worksheet, columns.map((c, i) => ({ value: c.header, style: "borderHead", merge: colgroup[i] })));

      excelSupporter.addTable(worksheet, {
        rowHeight: 18,
        colgroup,
        data: this.sortedList.map((item, idx) => columns.map((c) => c.value(item, idx))),
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },

    formatPhone(phone) {
      // 값이 없으면 빈 문자열
      if (!phone) {
        return "";
      }
      // 마스킹된 번호(* 포함)는 그대로 반환
      if (phone.indexOf("*") !== -1) {
        return phone;
      }
      // 숫자만 추출 후 3-4-4 / 3-3-4 형식으로 포맷
      const digits = phone.replace(/[^0-9]/g, "");
      if (digits.length === 11) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
      }
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      // 그 외 길이는 원본 그대로 반환
      return phone;
    },

    loadHistory() {
      // 이미 진행 중인 요청이 있으면 해당 Promise를 반환하여 중복 호출 방지
      if (this.loadingPromise) {
        return this.loadingPromise;
      }

      this.isLoading = true;

      const search = this.nav.value;
      const { startDate, endDate } = this.dateRange;
      const rangeKey = `${startDate}~${endDate}`;
      const options = {
        params: {
          projectId: this.projectId,
          boardIds: [976],
          type: "access",
          // 권한이 없으면 서버가 휴대폰번호를 빼고, 생년월일은 연도만 남겨(YY****) 내려준다
          privacyMode: "strict",
          // API 호출 시에는 사업장명과 기간만 전달 (나머지는 클라이언트에서 필터링)
          regionName: search.field && search.field.fieldName ? search.field.fieldName : "",
          dateFrom: `${startDate} 00:00:00`,
          dateTo: `${endDate} 23:59:59`,
        },
      };

      this.loadingPromise = HttpRequest.get("/api/v1/project/qr/history/status", options)
        .then(async (res) => {
          // 서버 응답은 사용자당 1건(스캔 목록 포함)이다. 여기서 사업장·날짜별 행으로 풀어낸다
          const result = [];

          for (const item of res.data ? res.data : []) {
            const companyName = item.user && item.user.company && item.user.company.com_name ? item.user.company.com_name : "";
            const userName = item.user && item.user.name ? item.user.name : "";
            const phone = item.user && item.user.phone ? this.formatPhone(item.user.phone) : "";
            const birth = item.user && item.user.birth ? item.user.birth : "";
            const userId = item.user && item.user.id ? item.user.id : "";

            const createdAtList = item.created_at_list ? item.created_at_list : [];
            const dataList = item.data_list ? item.data_list : [];
            const regionNameList = item.region_name_list ? item.region_name_list : [];
            const postIdList = item.post_ids ? item.post_ids : [];

            // 공사/작업명 및 작업기간 (permit 정보)
            const permit = item.permit ? item.permit : null;
            const permitName = permit && permit.permit_name ? permit.permit_name : "";
            let workPeriod = "";
            if (permit) {
              if (permit.is_added === true) {
                workPeriod = "당일";
              } else {
                const startDate = permit.start_date ? permit.start_date : "";
                const endDate = permit.end_date ? permit.end_date : "";
                workPeriod = `${startDate} ~ ${endDate}`;
              }
            }

            const base = { companyName, permitName, workPeriod, userName, birth, phone, userId };

            // 스캔 기록을 시간순으로 정렬한 뒤 사업장·날짜별로 묶고, 그 안에서 입문-출문 한 쌍씩 한 행으로 만든다
            // 하루에 여러 허가서로 여러 번 입출문한 경우를 허가서별 행으로 나누는 건 서버가 스캔별 허가서 정보를 주도록 바뀐 뒤 처리한다
            const scans = createdAtList.map((createdAt, index) => ({
              created_at: createdAt,
              data: dataList[index],
              post_id: postIdList[index],
              region_name: regionNameList[index] || "",
            }));
            scans.sort((a, b) => `${a.created_at}`.localeCompare(`${b.created_at}`));

            const groupByKey = {};
            const groups = [];
            for (const scan of scans) {
              const key = `${scan.region_name}_${`${scan.created_at}`.slice(0, 10)}`;
              if (!groupByKey[key]) {
                groupByKey[key] = { personKey: `${userId}_${key}`, regionName: scan.region_name, qrs: [] };
                groups.push(groupByKey[key]);
              }
              groupByKey[key].qrs.push(scan);
            }

            for (const group of groups) {
              for (const session of this.buildAccessSessions(group.qrs)) {
                result.push({ ...base, ...group, enterTime: session.enterTime, exitTime: session.exitTime, exitManual: false, postId: session.postId });
              }
            }
          }

          // QR 출문이 없는 행은 허가서에 수기로 입력된 출문시간으로 보완한다
          await this.applyManualExitTimes(result);

          this.canViewPhone = res.canViewUnmasked === true;

          // 전체 데이터를 allData에 저장
          this.allData = result;
          this.loadedRange = rangeKey;

          // 통계 및 페이지네이션 업데이트
          this.updateStatistics();
          this.updatePagination();

          this.loadedPage = this.page;

          this.isLoading = false;
        })
        .catch((err) => {
          console.error("QR 스캔 목록 조회 오류:", err);
          this.loadedRange = rangeKey; // 실패한 기간을 반복 호출하지 않는다 (갱신 버튼으로 재시도)
          this.isLoading = false;
          this.$vs.notify({
            title: "오류",
            text: "QR 스캔 목록을 불러오는 중 오류가 발생했습니다.",
            color: "danger",
            position: "top-right",
          });
        })
        .finally(() => {
          // 요청 완료 후 진행 중 Promise 해제 (다음 호출 허용)
          this.loadingPromise = null;
          // 요청 중에 기간이 바뀌었으면 바뀐 기간으로 다시 불러온다
          const { startDate: sd, endDate: ed } = this.dateRange;
          if (`${sd}~${ed}` !== this.loadedRange) {
            this.loadHistory();
          }
        });

      return this.loadingPromise;
    },

    onChangePage(page) {},
  },
};
</script>
<style lang="scss" scoped>
h5 {
  font-size: 16px;
  font-weight: 500;
  color: #3c3c3c;
}

table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 10px 7px;
  }

  th {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
    position: relative;

    &.sortable {
      cursor: pointer;
      user-select: none;
      transition: background-color 0.2s;

      &:hover {
        background: #e8e8e8;
      }

      .sort-icon {
        margin-left: 4px;
        font-size: 12px;
        color: #666;
      }
    }
  }

  tr.avg td {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
  }

  tbody tr {
    transition: background-color 0.2s;

    // 홀수 행
    &:nth-child(odd) {
      td {
        background: #ffffff;
      }
    }

    // 짝수 행
    &:nth-child(even) {
      td {
        background: #f8f9fa;
      }
    }

    // 마우스 오버 시
    &:hover {
      td {
        background: #e3f2fd !important;
      }
    }
  }

  td {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
  }

  .empty-message {
    padding: 40px 20px;
    color: #999;
    font-size: 15px;
  }
}

.range-info {
  font-size: 13px;
  color: #8a8a8a;
  white-space: nowrap;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;

  .loading-text {
    margin-top: 20px;
    font-size: 15px;
    color: #666;
  }
}

.card-grid {
  grid-template-columns: 1fr 1fr 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.responsive-grid {
  grid-template-columns: 1.8fr 1.2fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}
</style>
